/**
 * Stock Operations admin sayfası — Manuel hareket / Transfer / Fire kaydı.
 *
 * 3 ayrı modal: stok düzeltme, lokasyonlar arası transfer, fire kaydı.
 * Hepsi backend action endpoint'lerine bağlı.
 */
import { Alert, Anchor, Badge, Box, Card, Chip, Divider, Drawer, SimpleGrid, Stack, Tabs, Button, Group, Select, NumberInput, Textarea, Text, TextInput } from "@mantine/core";
import { StockItemPickerModal, type StockItemPickerRow } from "../components/StockItemPickerModal";
import { ProductPickerModal } from "../components/ProductPickerModal";
import { DatePickerInput } from "@mantine/dates";
import { IconTrash, IconArrowsRightLeft, IconAdjustments, IconSearch, IconCalendar, IconInfoCircle, IconToolsKitchen2 } from "@tabler/icons-react";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useResource, useInfiniteResource, apiClient } from "../../../shared/api";
import { PageHeader, EmptyState, formatDateTime, formatQty, InfiniteTable } from "../../../shared/ui";

const WASTE_REASON = [
  { value: "EXPIRED", label: "Son kullanım geçti" },
  { value: "DAMAGED", label: "Hasarlı" },
  { value: "SPILLED", label: "Döküldü" },
  { value: "PREP_ERROR", label: "Hazırlık hatası" },
  { value: "QUALITY", label: "Kalite kontrol" },
  { value: "OTHER", label: "Diğer" },
];

const MOVEMENT_COLOR: Record<string, string> = {
  RECEIVED: "green",
  PRODUCT_RECEIVED: "green",
  SOLD: "blue",
  PRODUCT_SOLD: "blue",
  WASTED: "red",
  STAFF_MEAL: "grape",
  TRANSFERRED_OUT: "orange",
  TRANSFERRED_IN: "teal",
  ADJUSTED: "violet",
  RETURNED: "yellow",
  CANCELLED_REVERSE: "gray",
  ORDER_CANCELLED_REVERSE: "gray",
  VOIDED: "gray",
  COMPLIMENTARY: "pink",
  RECONCILIATION: "indigo",
};

// Filtre Select'inde kullanılan etiketler — backend `movement_type_label`
// satır satır Türkçe display döner; bu map sadece dropdown seçenekleri için.
// Etiketler ana eylem ("Mal Kabul" / "Satış") + parantezde alt-tür biçiminde
// — backend `MovementCode` choices'larıyla aynı isimlendirme.
const MOVEMENT_LABELS: { value: string; label: string }[] = [
  { value: "PRODUCT_SOLD", label: "Satış (Ürün)" },
  { value: "SOLD", label: "Satış (Reçete)" },
  { value: "PRODUCT_RECEIVED", label: "Mal Kabul (Ürün)" },
  { value: "RECEIVED", label: "Mal Kabul (Hammadde)" },
  { value: "WASTED", label: "Fire" },
  { value: "STAFF_MEAL", label: "Personel Yemeği" },
  { value: "ADJUSTED", label: "Manuel Düzeltme" },
  { value: "TRANSFERRED_IN", label: "Transfer (Geliş)" },
  { value: "TRANSFERRED_OUT", label: "Transfer (Çıkış)" },
  { value: "ORDER_CANCELLED_REVERSE", label: "Sipariş İptal — Geri Yaz" },
  { value: "RETURNED", label: "Müşteri İadesi" },
  { value: "VOIDED", label: "Sipariş Void" },
  { value: "COMPLIMENTARY", label: "İkram" },
  { value: "RECONCILIATION", label: "Mutabakat Düzeltme" },
];

// Hızlı tarih chip'leri — günlük operasyon için kısayollar.
function quickRange(key: string): [Date | null, Date | null] {
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  if (key === "today") return [startOfDay(now), endOfDay(now)];
  if (key === "yesterday") {
    const y = new Date(now); y.setDate(y.getDate() - 1);
    return [startOfDay(y), endOfDay(y)];
  }
  if (key === "7d") {
    const s = new Date(now); s.setDate(s.getDate() - 7);
    return [startOfDay(s), endOfDay(now)];
  }
  if (key === "30d") {
    const s = new Date(now); s.setDate(s.getDate() - 30);
    return [startOfDay(s), endOfDay(now)];
  }
  return [null, null];
}

function navigate(page: string, params?: Record<string, any>) {
  window.dispatchEvent(
    new CustomEvent("admin:navigate", { detail: { page, params } }),
  );
}

export function StockOperationsPage() {
  const [adjustOpen, adjustHandlers] = useDisclosure(false);
  const [transferOpen, transferHandlers] = useDisclosure(false);
  const [wasteOpen, wasteHandlers] = useDisclosure(false);
  const [staffMealOpen, staffMealHandlers] = useDisclosure(false);

  return (
    // miw=0: flex item olarak Main içinde içeriğin (geniş tablo) viewport'u
    // aşıp tüm sayfayı yatay kaydırmasını engeller — taşma tablo içinde kalır.
    <Stack gap="md" style={{ minWidth: 0 }}>
      <PageHeader
        title="Stok Operasyonları"
        subtitle="Düzeltme, transfer, fire, personel yemeği"
        actions={
          // Dar ekranda 4 buton tek satıra sığmaz → sarsın (nowrap taşmaya
          // ve sayfa genelinde yatay kaymaya yol açıyordu).
          <Group gap="xs" wrap="wrap">
            <Button leftSection={<IconAdjustments size={16} />} onClick={adjustHandlers.open}>Manuel Düzeltme</Button>
            <Button leftSection={<IconArrowsRightLeft size={16} />} onClick={transferHandlers.open} variant="default">Transfer</Button>
            <Button leftSection={<IconTrash size={16} />} onClick={wasteHandlers.open} color="red" variant="light">Fire</Button>
            <Button leftSection={<IconToolsKitchen2 size={16} />} onClick={staffMealHandlers.open} color="grape" variant="light">Personel Yemeği</Button>
          </Group>
        }
      />

      <Tabs defaultValue="movements">
        {/* Dar ekranda sekmeler sığmazsa yatay kaydırılsın — sarmasın
            (flexWrap:nowrap), taşma sayfayı değil yalnız bu şeridi kaydırsın. */}
        <Tabs.List
          style={{ flexWrap: "nowrap", overflowX: "auto" }}
        >
          <Tabs.Tab value="movements">Hareketler (Ledger)</Tabs.Tab>
          <Tabs.Tab value="waste">Fire Kayıtları</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="movements" pt="md">
          <MovementsList />
        </Tabs.Panel>
        <Tabs.Panel value="waste" pt="md">
          <WasteList />
        </Tabs.Panel>
      </Tabs>

      <AdjustModal opened={adjustOpen} onClose={adjustHandlers.close} />
      <TransferModal opened={transferOpen} onClose={transferHandlers.close} />
      <WasteModal opened={wasteOpen} onClose={wasteHandlers.close} />
      <StaffMealModal opened={staffMealOpen} onClose={staffMealHandlers.close} />
    </Stack>
  );
}

function MovementsList() {
  const [search, setSearch] = useState("");
  const [movementType, setMovementType] = useState<string | null>(null);
  // Açılışta bugünün hareketleri seçili gelsin — günlük operasyon varsayılanı.
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    () => quickRange("today"),
  );
  const [activeChip, setActiveChip] = useState<string | null>("today");
  const [debouncedSearch] = useDebouncedValue(search.trim(), 350);

  const filters = useMemo(() => {
    const f: Record<string, any> = {};
    if (debouncedSearch) f.search = debouncedSearch;
    if (movementType) f.movement_type = movementType;
    if (dateRange[0]) f.created_at__gte = dateRange[0].toISOString();
    if (dateRange[1]) f.created_at__lte = dateRange[1].toISOString();
    return f;
  }, [debouncedSearch, movementType, dateRange]);

  const hasFilter = Object.keys(filters).length > 0;

  const {
    items: list,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("stock-movements", { filters });

  // Backend agregat — filtre setinin tamamı üzerinden (yüklü scroll değil)
  const summary = useQuery({
    queryKey: ["stock-movements", "summary", filters],
    queryFn: async () => {
      const { data } = await apiClient.get(
        "/api/v1/stock-movements/summary/", { params: filters },
      );
      return data as {
        total_in: string; total_out: string; net: string; count: number;
      };
    },
    enabled: hasFilter,
  });

  const applyChip = (key: string) => {
    if (activeChip === key) {
      setActiveChip(null);
      setDateRange([null, null]);
    } else {
      setActiveChip(key);
      setDateRange(quickRange(key));
    }
  };

  const clearAll = () => {
    setSearch("");
    setMovementType(null);
    setDateRange([null, null]);
    setActiveChip(null);
  };

  return (
    <Stack gap="sm" style={{ minWidth: 0 }}>
      {/* Filtre satırı */}
      {/* Arama tam genişlikte üst satırda; tip + tarih altta yan yana
          (mobilde sarar). Önceden hepsi nowrap tek satırdaydı → mobilde
          arama kutusu ikona kadar sıkışıyordu. */}
      <Stack gap="sm">
        <TextInput
          placeholder="Ürün/hammadde adı veya SKU ile ara..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
        <Group gap="sm" align="flex-end" grow wrap="wrap" style={{ minWidth: 0 }}>
          <Select
            placeholder="Hareket Tipi"
            data={MOVEMENT_LABELS}
            value={movementType}
            onChange={setMovementType}
            clearable
            searchable
          />
          <DatePickerInput
            type="range"
            placeholder="Tarih aralığı"
            leftSection={<IconCalendar size={16} />}
            value={dateRange}
            onChange={(v) => {
              setDateRange(v as [Date | null, Date | null]);
              setActiveChip(null);  // manuel tarih → chip pasif
            }}
            clearable
            locale="tr"
            valueFormat="DD.MM.YYYY"
          />
        </Group>
      </Stack>

      {/* Hızlı tarih chip'leri */}
      <Group gap="xs">
        {[
          { key: "today", label: "Bugün" },
          { key: "yesterday", label: "Dün" },
          { key: "7d", label: "Son 7 Gün" },
          { key: "30d", label: "Son 30 Gün" },
        ].map((c) => (
          <Chip
            key={c.key}
            checked={activeChip === c.key}
            onClick={() => applyChip(c.key)}
            size="xs"
            variant="light"
          >
            {c.label}
          </Chip>
        ))}
        {hasFilter && (
          <Button size="xs" variant="subtle" onClick={clearAll}>
            Filtreleri temizle
          </Button>
        )}
      </Group>

      {/* Filtreli özet — backend agregatı */}
      {hasFilter && summary.data && (
        <Card padding="md" radius="md" withBorder bg="gray.0">
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <div>
              <Text size="xs" c="dimmed">Toplam Giriş</Text>
              <Text size="lg" fw={700} c="green">
                +{formatQty(summary.data.total_in)}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Toplam Çıkış</Text>
              <Text size="lg" fw={700} c="red">
                {formatQty(summary.data.total_out)}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Net</Text>
              <Text
                size="lg"
                fw={700}
                c={parseFloat(summary.data.net) >= 0 ? "green" : "red"}
              >
                {parseFloat(summary.data.net) > 0 ? "+" : ""}
                {formatQty(summary.data.net)}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Kayıt Sayısı</Text>
              <Text size="lg" fw={700}>{summary.data.count}</Text>
            </div>
          </SimpleGrid>
        </Card>
      )}

      <Card padding="sm" radius="md" withBorder miw={0}>
        <InfiniteTable
          rows={list}
          rowKey={(m) => m.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          emptyContent={<EmptyState title="Hareket yok" />}
          columns={[
            { header: "Tarih", render: (m) => formatDateTime(m.created_at) },
            {
              header: "Tip",
              render: (m) => (
                <Badge
                  color={MOVEMENT_COLOR[m.movement_type] || "gray"}
                  size="sm"
                  variant="light"
                >
                  {m.movement_type_label || m.movement_type}
                </Badge>
              ),
            },
            {
              header: "Malzeme",
              render: (m) => {
                const name = m.item_name || "—";
                if (m.item_kind === "PRODUCT" || m.item_kind === "INGREDIENT") {
                  return (
                    <Anchor
                      fw={500}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate("stock-items", {
                        search: name,
                        kind: m.item_kind,
                      })}
                      title="Stok sayfasında bu kalemi göster"
                    >
                      {name}
                    </Anchor>
                  );
                }
                return name;
              },
            },
            {
              header: "Lokasyon",
              render: (m) => (
                <Text size="sm" c="dimmed">
                  {m.item_location_name || "—"}
                </Text>
              ),
            },
            {
              header: "Miktar",
              align: "right" as const,
              render: (m) => {
                const delta = parseFloat(m.quantity_delta);
                const positive = delta >= 0;
                const unit = m.item_unit || "";
                return (
                  <Text fw={700} span c={positive ? "green" : "red"}>
                    {positive ? "+" : ""}{formatQty(m.quantity_delta)} {unit}
                  </Text>
                );
              },
            },
            {
              header: "Bakiye",
              align: "right" as const,
              render: (m) => (
                <Text
                  size="sm"
                  fw={500}
                  title={`Bu hareket sonrası ${m.item_location_name || "lokasyon"} bakiyesi`}
                >
                  {formatQty(m.balance_after)} {m.item_unit}
                </Text>
              ),
            },
            {
              header: "Referans",
              render: (m) => {
                const label = m.reference_label || "";
                if (!label) return <Text size="sm">—</Text>;
                const rt = (m.reference_type || "").toLowerCase();
                // Sipariş satırı → Siparişler sayfasında drawer aç.
                // reference_id OrderLine'ın id'sidir; Orders sayfası
                // Order.id beklediği için reference_label içindeki
                // "Sipariş #external_reference veya id-prefix" göstergesi
                // kullanılır — ama detay drawer açmak için Order id'ye
                // ihtiyaç var. Backend serializer'a `order_id` eklemediğimiz
                // için şimdilik search ile yönlendiriyoruz.
                if (rt === "order_line") {
                  // Etiket "Sipariş #abc · Masa 3 · Adisyon a1b2" olabilir —
                  // arama için yalnız ilk parçadaki sipariş ref'ini al.
                  const ref = label
                    .split(" · ")[0]
                    .replace(/^Sipariş\s+#?/, "")
                    .trim();
                  return (
                    <Anchor
                      size="sm"
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate("orders", { search: ref })}
                      title="Siparişler sayfasında ara"
                    >
                      {label}
                    </Anchor>
                  );
                }
                if (rt === "procurement.goods_receipt" || rt === "goods_receipt") {
                  // "Mal Girişi GR-202605-0004"
                  const gr = label.replace(/^Mal\s+Girişi\s+/, "").trim();
                  return (
                    <Anchor
                      size="sm"
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate("gr", { search: gr })}
                      title="Mal Girişleri sayfasında ara"
                    >
                      {label}
                    </Anchor>
                  );
                }
                return <Text size="sm">{label}</Text>;
              },
            },
            { header: "Not", render: (m) => m.notes_label || m.notes || "—" },
            { header: "Kullanıcı", render: (m) => m.actor_username || "sistem" },
          ]}
        />
      </Card>
    </Stack>
  );
}

// reason kodu → Türkçe etiket (fire movement'ı `notes` alanında sebep kodunu
// taşır — services.record_movement(notes=req.reason)).
const WASTE_REASON_LABEL: Record<string, string> = Object.fromEntries(
  WASTE_REASON.map((r) => [r.value, r.label]),
);

function WasteList() {
  // Fire kayıtları doğrudan ledger'dan (WASTED hareketleri) okunur — eski
  // WasteRecord tablosu yalnız hammadde + onaylanmış kayıtları tutuyordu ve
  // ürün fire'ını / Sprint 36 öncesini kaçırıyordu. Ledger tek doğruluk
  // kaynağı: Hareketler sekmesinde görünen her fire burada da görünür.
  const {
    items: list,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("stock-movements", {
    filters: { movement_type: "WASTED" },
  });

  return (
    <Card padding="sm" radius="md" withBorder>
      <InfiniteTable
        rows={list}
        rowKey={(m) => m.id}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        emptyContent={<EmptyState title="Fire kaydı yok" />}
        columns={[
          { header: "Tarih", render: (m) => formatDateTime(m.created_at) },
          { header: "Malzeme", render: (m) => m.item_name || "—" },
          { header: "Lokasyon", render: (m) => m.item_location_name || "—" },
          {
            header: "Miktar",
            align: "right" as const,
            render: (m) => (
              <Text fw={700} span c="red">
                {formatQty(m.quantity_delta)} {m.item_unit || ""}
              </Text>
            ),
          },
          {
            header: "Sebep",
            render: (m) => {
              // notes_label sunucuda zaten temizlenir: ham sebep kodu Türkçe'ye
              // çevrilir + " (order_line=<uuid>)" eki atılır. Ham notes'a
              // (UUID'li) düşmeyiz; yalnız bare-kod kaydı için sözlüğe bak.
              const reason = m.notes_label || m.notes || "";
              if (!reason) return "—";
              return (
                <Badge color="red" variant="light">
                  {WASTE_REASON_LABEL[reason] || reason}
                </Badge>
              );
            },
          },
          { header: "Kullanıcı", render: (m) => m.actor_username || "sistem" },
        ]}
      />
    </Card>
  );
}

function AdjustModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const [picked, setPicked] = useState<StockItemPickerRow | null>(null);
  const [pickerOpen, pickerHandlers] = useDisclosure(false);
  const [delta, setDelta] = useState<number | string>(0);
  const [notes, setNotes] = useState("");
  const qc = useQueryClient();

  const reset = () => {
    setPicked(null);
    setDelta(0);
    setNotes("");
  };

  const mutation = useMutation({
    mutationFn: async () => (await apiClient.post("/api/v1/inventory/adjust/", {
      stock_item_id: picked?.id,
      delta: String(delta),
      notes,
    })).data,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["stock-items"] });
      qc.invalidateQueries({ queryKey: ["stock-movements"] });
      qc.invalidateQueries({ queryKey: ["adjustment-requests"] });
      if (data?.needs_approval) {
        notifications.show({
          title: "Onaya gönderildi",
          message: "Bu düzeltme eşik üstünde — yönetici onayı bekliyor. " +
            "Onaylanınca stok güncellenir.",
          color: "orange",
        });
      } else {
        notifications.show({ message: "Stok güncellendi", color: "green" });
      }
      reset();
      onClose();
    },
    onError: (e: any) => notifications.show({
      message: e?.response?.data?.detail || "Hata", color: "red",
    }),
  });

  const pickedName = picked
    ? (picked.product_name || picked.ingredient_name || "(adsız)")
    : null;

  return (
    <>
      <Drawer
        opened={opened}
        onClose={() => { reset(); onClose(); }}
        title={<Text fw={700} size="lg">Manuel Stok Düzeltmesi</Text>}
        position="right"
        size="md"
        padding="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Sayım sonrası fark, hata düzeltmesi vs. hareket ledger'a kalıcı
            olarak yazılır; eşik üstü değişikliklerde yönetici onayı istenir.
          </Text>
          <Divider />

          {/* Kalem seçici — büyük datasette dropdown şişmesin diye
              ayrı modaldan server-side aramayla seçilir.
              Hem hammadde hem hazır ürün düzeltilebilir (sayım sonrası
              "1 kola fazladan bulduk" gibi senaryolar için ürünler de). */}
          <Box>
            <Text size="sm" fw={500} mb={4}>
              Hammadde / Ürün × Lokasyon <Text span c="red">*</Text>
            </Text>
            <Button
              variant="light"
              leftSection={<IconSearch size={14} />}
              onClick={pickerHandlers.open}
              fullWidth
              justify="flex-start"
              size="md"
            >
              {picked ? (
                <Group gap={6} wrap="nowrap">
                  <Text fw={500}>{pickedName}</Text>
                  {picked.kind === "PRODUCT" && (
                    <Badge size="xs" color="violet" variant="light">Ürün</Badge>
                  )}
                  {picked.kind === "INGREDIENT" && (
                    <Badge size="xs" color="blue" variant="light">Hammadde</Badge>
                  )}
                  <Text size="xs" c="dimmed">
                    @ {picked.location_name || "—"} · mevcut {formatQty(picked.quantity)} {picked.unit || ""}
                  </Text>
                </Group>
              ) : (
                "Stok kalemi seç..."
              )}
            </Button>
          </Box>

          <NumberInput
            label="Delta (+ekle / -çıkar)"
            description={
              picked?.unit
                ? `Birim: ${picked.unit} · Eklemek için pozitif, çıkarmak için negatif değer girin`
                : "Önce stok kalemi seçin"
            }
            value={delta}
            onChange={setDelta}
            decimalScale={3}
            required
            disabled={!picked}
            size="md"
          />
          <Textarea
            label="Açıklama"
            placeholder="Ne için düzeltildiğini açıklayın (sayım farkı, yanlış giriş vs.)"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            required
            minRows={3}
            autosize
          />

          <Divider />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { reset(); onClose(); }}>
              İptal
            </Button>
            <Button
              onClick={() => mutation.mutate()}
              loading={mutation.isPending}
              disabled={
                !picked || !notes
                || !delta || Number(delta) === 0
              }
            >
              Kaydet
            </Button>
          </Group>
        </Stack>
      </Drawer>

      <StockItemPickerModal
        opened={pickerOpen}
        onClose={pickerHandlers.close}
        onSelect={(item) => setPicked(item)}
        title="Düzeltilecek Stok Kalemini Seç"
      />
    </>
  );
}

function TransferModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { data: locData } = useResource<any>("stock-locations");
  const locs: any[] = locData?.results || locData || [];

  // Kaynak: StockItem picker (hammadde × kaynak lokasyon). Picker ile
  // olmayan stoğun transferini engelliyoruz; mevcut miktar da görünür.
  const [picked, setPicked] = useState<StockItemPickerRow | null>(null);
  const [pickerOpen, pickerHandlers] = useDisclosure(false);
  const [to, setTo] = useState<string | null>(null);
  const [qty, setQty] = useState<number | string>(0);
  const [notes, setNotes] = useState("");
  const qc = useQueryClient();

  const reset = () => {
    setPicked(null);
    setTo(null);
    setQty(0);
    setNotes("");
  };

  const mutation = useMutation({
    mutationFn: async () => (await apiClient.post("/api/v1/inventory/transfer/", {
      ingredient_id: picked?.ingredient,
      from_location_id: picked?.location,
      to_location_id: to,
      quantity: String(qty),
      notes,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stock-items"] });
      qc.invalidateQueries({ queryKey: ["stock-movements"] });
      notifications.show({ message: "Transfer yapıldı", color: "green" });
      reset();
      onClose();
    },
    onError: (e: any) => notifications.show({
      message: e?.response?.data?.detail || "Hata", color: "red",
    }),
  });

  const pickedName = picked
    ? (picked.product_name || picked.ingredient_name || "(adsız)")
    : null;

  return (
    <>
      <Drawer
        opened={opened}
        onClose={() => { reset(); onClose(); }}
        title={<Text fw={700} size="lg">Lokasyonlar Arası Transfer</Text>}
        position="right"
        size="md"
        padding="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Bir hammaddeyi kaynak lokasyondan hedef lokasyona taşır.
            Kaynakta TRANSFERRED_OUT, hedefte TRANSFERRED_IN hareketi yazılır.
          </Text>
          <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
            Yalnız <b>hammadde transferi</b> desteklenir (un, peynir, et vb.).
            Hazır ürünleri (Cola, su gibi) lokasyonlar arası taşımak için
            <i> Manuel Düzeltme</i> kullanabilirsiniz.
          </Alert>
          <Divider />

          {/* Kaynak picker — kalem × kaynak lokasyon birlikte seçilir.
              Yalnız hammadde transferi (ürünler için ayrı flow). */}
          <Box>
            <Text size="sm" fw={500} mb={4}>
              Kaynak (Hammadde × Lokasyon) <Text span c="red">*</Text>
            </Text>
            <Button
              variant="light"
              leftSection={<IconSearch size={14} />}
              onClick={pickerHandlers.open}
              fullWidth
              justify="flex-start"
              size="md"
            >
              {picked ? (
                <Group gap={6} wrap="nowrap">
                  <Text fw={500}>{pickedName}</Text>
                  <Text size="xs" c="dimmed">
                    @ {picked.location_name || "—"} · mevcut {formatQty(picked.quantity)} {picked.unit || ""}
                  </Text>
                </Group>
              ) : (
                "Hammadde ve kaynak lokasyon seç..."
              )}
            </Button>
          </Box>

          <Select
            label="Hedef Lokasyon"
            required
            data={locs
              .filter((l) => l.id !== picked?.location)
              .map((l) => ({ value: l.id, label: l.name }))}
            value={to}
            onChange={setTo}
            disabled={!picked}
            description={!picked ? "Önce kaynak seçin" : undefined}
          />
          <NumberInput
            label="Miktar"
            description={
              picked?.unit
                ? `Birim: ${picked.unit} · Mevcut: ${formatQty(picked.quantity)}`
                : undefined
            }
            value={qty}
            onChange={setQty}
            min={0.001}
            decimalScale={3}
            required
            disabled={!picked}
          />
          <Textarea
            label="Not (opsiyonel)"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            autosize
            minRows={2}
          />

          <Divider />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { reset(); onClose(); }}>
              İptal
            </Button>
            <Button
              onClick={() => mutation.mutate()}
              loading={mutation.isPending}
              disabled={
                !picked?.ingredient || !to || !qty || Number(qty) <= 0
              }
            >
              Transfer Et
            </Button>
          </Group>
        </Stack>
      </Drawer>

      <StockItemPickerModal
        opened={pickerOpen}
        onClose={pickerHandlers.close}
        onSelect={(item) => setPicked(item)}
        kind="INGREDIENT"
        title="Transfer Edilecek Hammadde × Kaynak"
      />
    </>
  );
}

function WasteModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  // Picker ile hammadde × lokasyon birlikte seçilir — backend endpoint
  // (ingredient_id + location_id) iki ayrı id istiyor; StockItem zaten
  // ikisini birleştiriyor, picker'dan tek seçim ikisini de getirir.
  const [picked, setPicked] = useState<StockItemPickerRow | null>(null);
  const [pickerOpen, pickerHandlers] = useDisclosure(false);
  const [qty, setQty] = useState<number | string>(0);
  const [reason, setReason] = useState<string>("OTHER");
  const [notes, setNotes] = useState("");
  const qc = useQueryClient();

  const reset = () => {
    setPicked(null);
    setQty(0);
    setReason("OTHER");
    setNotes("");
  };

  const mutation = useMutation({
    mutationFn: async () => (await apiClient.post("/api/v1/inventory/waste/", {
      ingredient_id: picked?.ingredient,
      location_id: picked?.location,
      quantity: String(qty),
      reason,
      notes,
    })).data,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["stock-items"] });
      qc.invalidateQueries({ queryKey: ["stock-movements"] });
      qc.invalidateQueries({ queryKey: ["waste"] });
      qc.invalidateQueries({ queryKey: ["adjustment-requests"] });
      if (data?.needs_approval) {
        notifications.show({
          title: "Onaya gönderildi",
          message: "Bu fire eşik üstünde — yönetici onayı bekliyor.",
          color: "orange",
        });
      } else {
        notifications.show({ message: "Fire kaydedildi, stok güncellendi", color: "green" });
      }
      reset();
      onClose();
    },
    onError: (e: any) => notifications.show({
      message: e?.response?.data?.detail || "Hata", color: "red",
    }),
  });

  const pickedName = picked
    ? (picked.product_name || picked.ingredient_name || "(adsız)")
    : null;

  return (
    <>
      <Drawer
        opened={opened}
        onClose={() => { reset(); onClose(); }}
        title={<Text fw={700} size="lg">Fire Kaydı</Text>}
        position="right"
        size="md"
        padding="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Bozulan, dökülen veya kullanılamaz hâle gelen ürünleri kaydeder.
            Sebep, raporlama için gerekli; eşik üstü değerler yönetici onayına gider.
          </Text>
          <Divider />

          {/* Hammadde × lokasyon picker — büyük datasette dropdown şişmesin. */}
          <Box>
            <Text size="sm" fw={500} mb={4}>
              Malzeme × Lokasyon <Text span c="red">*</Text>
            </Text>
            <Button
              variant="light"
              leftSection={<IconSearch size={14} />}
              onClick={pickerHandlers.open}
              fullWidth
              justify="flex-start"
              size="md"
            >
              {picked ? (
                <Group gap={6} wrap="nowrap">
                  <Text fw={500}>{pickedName}</Text>
                  <Text size="xs" c="dimmed">
                    @ {picked.location_name || "—"} · mevcut {formatQty(picked.quantity)} {picked.unit || ""}
                  </Text>
                </Group>
              ) : (
                "Fire edilecek hammaddeyi seç..."
              )}
            </Button>
          </Box>

          <NumberInput
            label="Miktar"
            description={
              picked?.unit
                ? `Birim: ${picked.unit} · Mevcut: ${formatQty(picked.quantity)}`
                : "Önce malzeme seçin"
            }
            value={qty}
            onChange={setQty}
            min={0.001}
            decimalScale={3}
            required
            disabled={!picked}
          />
          <Select
            label="Sebep"
            required
            data={WASTE_REASON}
            value={reason}
            onChange={(v) => v && setReason(v)}
          />
          <Textarea
            label="Açıklama"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            autosize
            minRows={2}
          />

          <Divider />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { reset(); onClose(); }}>
              İptal
            </Button>
            <Button
              color="red"
              onClick={() => mutation.mutate()}
              loading={mutation.isPending}
              disabled={
                !picked?.ingredient || !picked?.location
                || !qty || Number(qty) <= 0
              }
            >
              Fire Kaydet
            </Button>
          </Group>
        </Stack>
      </Drawer>

      <StockItemPickerModal
        opened={pickerOpen}
        onClose={pickerHandlers.close}
        onSelect={(item) => setPicked(item)}
        kind="INGREDIENT"
        title="Fire Edilecek Hammadde × Lokasyon"
      />
    </>
  );
}

// Personel yemeği sebep/tip kodları — backend inventory.StaffMealType ile aynı.
const STAFF_MEAL_TYPE = [
  { value: "EMPLOYEE", label: "Personel Yemeği" },
  { value: "TASTING", label: "Tadım / Kalite" },
  { value: "OTHER", label: "Diğer" },
];

function StaffMealModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  // Personel yemeği ÜRÜN bazlı: ürünün BOM'u patlatılıp malzemeler STAFF_MEAL
  // olarak stoktan düşer (backend record_staff_meal). POS'a/ciroya gitmez.
  const [product, setProduct] = useState<{ id: string; name: string } | null>(null);
  const [qty, setQty] = useState<number | string>(1);
  const [mealType, setMealType] = useState("EMPLOYEE");
  const [notes, setNotes] = useState("");
  const [pickerOpen, pickerHandlers] = useDisclosure(false);
  const qc = useQueryClient();

  const reset = () => {
    setProduct(null);
    setQty(1);
    setMealType("EMPLOYEE");
    setNotes("");
  };

  const mutation = useMutation({
    mutationFn: async () => (await apiClient.post("/api/v1/staff-meals/", {
      product_id: product?.id,
      quantity: Number(qty),
      meal_type: mealType,
      notes,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stock-items"] });
      qc.invalidateQueries({ queryKey: ["stock-movements"] });
      notifications.show({ message: "Personel yemeği kaydedildi, stok düşüldü", color: "green" });
      reset();
      onClose();
    },
    onError: (e: any) => notifications.show({
      message: e?.response?.data?.detail || "Hata", color: "red",
    }),
  });

  return (
    <>
      <Drawer
        opened={opened}
        onClose={() => { reset(); onClose(); }}
        title={<Text fw={700} size="lg">Personel Yemeği</Text>}
        position="right"
        size="md"
        padding="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Personelin yediği menü ürününü kaydeder — ürünün reçetesi (BOM)
            patlatılıp malzemeler stoktan düşer. POS'a/ciroya GİTMEZ; maliyet
            food cost'tan ayrı "personel gideri" olarak raporlanır.
          </Text>
          <Divider />

          <Box>
            <Text size="sm" fw={500} mb={4}>
              Ürün <Text span c="red">*</Text>
            </Text>
            <Button
              variant="light"
              leftSection={<IconSearch size={14} />}
              onClick={pickerHandlers.open}
              fullWidth
              justify="flex-start"
              size="md"
            >
              {product ? product.name : "Personel yemeği ürününü seç..."}
            </Button>
          </Box>

          <NumberInput
            label="Porsiyon"
            value={qty}
            onChange={setQty}
            min={1}
            allowDecimal={false}
            required
          />
          <Select
            label="Tip"
            required
            data={STAFF_MEAL_TYPE}
            value={mealType}
            onChange={(v) => v && setMealType(v)}
          />
          <Textarea
            label="Açıklama"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            autosize
            minRows={2}
          />

          <Divider />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { reset(); onClose(); }}>
              İptal
            </Button>
            <Button
              color="grape"
              onClick={() => mutation.mutate()}
              loading={mutation.isPending}
              disabled={!product?.id || !qty || Number(qty) <= 0}
            >
              Kaydet
            </Button>
          </Group>
        </Stack>
      </Drawer>

      <ProductPickerModal
        opened={pickerOpen}
        onClose={pickerHandlers.close}
        onSelect={(p: any) => setProduct({ id: p.id, name: p.name })}
      />
    </>
  );
}
